import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";
import { FormRecordProvider } from "../../../../common/controls/forms/FormRecordContext";
import FormExtras from "../../../../common/components/formExtras";
import { DownloadIcon } from "../../../../icons";
import { VisibilityIcon } from "../../../../icons";
import { PDFViewerDialog } from "../../../../common/components/PDFViewerDialog";
import { isOriginalCrudStatus, RowCrudStatus } from "../../../common/types";
import { PhonogramUpdateStore } from "../../store";
import { runInAction } from "mobx";
import { InputString } from "../../../common/controls";
import { InputReferenceSet } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { InterpreterCreate } from "../../../interpreter/components/create/InterpreterCreate";
import { InterpreterCreateStore } from "../../../interpreter/store";
import { InputNumber } from "../../../common/controls";
import { PhonogramIsrcCustomField } from "../custom/PhonogramIsrcCustomField";
import { FileCRUDType } from "../../../common/types";
import { GridChangeSet } from "../../../common/types";
import { LoadFilesCRUDCompact } from "../../../common/controls";
import { InputBoolean } from "../../../common/controls";
import { ItemsCRUDCompact } from "../../../common/controls";
import { CrudRow } from "../../../common/types";
import { MusicalWorkLinkUpdate } from "../../../musicalWorkLink/components/update/MusicalWorkLinkUpdate";
import { MusicalWorkLinkUpdateStore } from "../../../musicalWorkLink/store";
import { GridColumn } from "../../../common/types";
import { InputText } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type PhonogramUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  recordId?: string;
  trackName: string;
  trackNameSelectedValue?: { displayId: string; id: string } | null;
  onTrackNameChange?: (value: string) => void;
  trackNameError?: string;
  performersSelectedValues: { displayId: string; id: string }[];
  onPerformersChange?: (value: string) => void;
  onPerformersSelected?: (value: { displayId: string; id: string }[]) => void;
  performersOptions?: { displayId: string; id: string }[];
  performersError?: string;
  albumName: string;
  albumNameSelectedValue?: { displayId: string; id: string } | null;
  onAlbumNameChange?: (value: string) => void;
  albumNameError?: string;
  publicationYear: number | undefined;
  publicationYearSelectedValue?: { displayId: string; id: string } | null;
  onPublicationYearChange?: (value: number | undefined) => void;
  publicationYearError?: string;
  isrc: string;
  isrcSelectedValue?: { displayId: string; id: string } | null;
  onIsrcChange?: (value: string) => void;
  isrcError?: string;
  originalProducer: string;
  originalProducerSelectedValue?: { displayId: string; id: string } | null;
  onOriginalProducerChange?: (value: string) => void;
  originalProducerError?: string;
  audioFileOnRestoreFile: (id: string) => void;
  audioFileOnLoadFile: (file: any) => void;
  audioFileOnCommitFile: (changes: GridChangeSet) => void;
  audioFileFiles: FileCRUDType[];
  audioFileError?: string;
  mainAudio: boolean;
  mainAudioSelectedValue?: { displayId: string; id: string } | null;
  onMainAudioChange?: (value: boolean) => void;
  mainAudioError?: string;
  linksOnRestore: (id: string) => void;
  linksOnCommitChanges: (changes: GridChangeSet) => void;
  linksOnCommitInlineChange: (
    rowId: string,
    changes: Record<string, any>
  ) => void;
  linksValidateChanges: (changes: GridChangeSet) => boolean;
  linksRows: CrudRow[];
  linksError?: string;
  musicalWorkLinkUpdateStore: MusicalWorkLinkUpdateStore;
  linksVisibleColumns: GridColumn[];
  linksColumnTypes: Record<
    string,
    {
      fieldType:
        | "String"
        | "Int"
        | "Decimal"
        | "Boolean"
        | "Date"
        | "DateTime"
        | "__Reference"
        | "File"
        | "__Enum";
      nullable?: boolean;
      readOnly?: boolean;
    }
  >;
  notes: string;
  notesSelectedValue?: { displayId: string; id: string } | null;
  onNotesChange?: (value: string) => void;
  notesError?: string;
  dummyCRUDRestoredObservable: number;
  phonogramUpdateStore: PhonogramUpdateStore;
};

export const PhonogramUpdateForm: React.FC<PhonogramUpdateFormProps> = observer(
  ({
    onSave = () => {},
    onCancel = () => {},
    saving = false,
    showToolbox = true,
    showSaveFooter,
    recordId,
    trackName = "",
    trackNameError,
    onTrackNameChange = () => {},
    onPerformersSelected = () => {},
    performersOptions = [],
    performersSelectedValues,
    performersError,
    onPerformersChange = () => {},
    albumName = "",
    albumNameError,
    onAlbumNameChange = () => {},
    publicationYear = undefined,
    publicationYearError,
    onPublicationYearChange = () => {},
    isrc = "",
    isrcError,
    onIsrcChange = () => {},
    originalProducer = "",
    originalProducerError,
    onOriginalProducerChange = () => {},
    audioFileOnRestoreFile,
    audioFileOnLoadFile,
    audioFileOnCommitFile,
    audioFileFiles,
    audioFileError,
    mainAudio = false,
    mainAudioError,
    onMainAudioChange = () => {},
    linksOnRestore,
    linksOnCommitChanges,
    linksOnCommitInlineChange,
    linksValidateChanges,
    linksRows,
    linksError,
    musicalWorkLinkUpdateStore,
    linksVisibleColumns,
    linksColumnTypes,
    notes = "",
    notesError,
    onNotesChange = () => {},
    dummyCRUDRestoredObservable,
    phonogramUpdateStore,
  }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (musicalWorkLinkUpdateStore.status === "uninitialized") {
      musicalWorkLinkUpdateStore.initialize();
    }

    const getViewAction = (
      onDownload: (props: {
        id: string;
        type: string;
        name: string;
        __status__: RowCrudStatus;
      }) => void,
      onViewPdf?: (props: any) => void
    ) => {
      const actions: any[] = [];
      if (onViewPdf) {
        actions.push({
          columnName: "__action_view_pdf",
          onClick: (props: any) =>
            isOriginalCrudStatus(props.__status__) ? onViewPdf(props) : {},
          icon: <VisibilityIcon />,
          label: langStore.viewCommand,
          isActionEnabled: (props: any) =>
            props.name?.toLowerCase().endsWith(".pdf"),
        });
      }
      actions.push({
        columnName: "__action_view",
        onClick: (props: any) =>
          isOriginalCrudStatus(props.__status__) ? onDownload(props) : {},
        icon: <DownloadIcon />,
        label: langStore.downloadCommand,
      });
      return actions;
    };
    return (
      <Grid>
        {showToolbox && (
          <FormToolbox
            saveAndCloseLabel={langStore.saveEntity}
            closeLabel={langStore.cancelUpdatedEntity}
            saveForm={() => onSave()}
            saveDisabled={saving}
            closeForm={() => onCancel()}
          />
        )}
        <Box>
          <FormRecordProvider
            recordId={recordId}
            values={{
              trackName,
              albumName,
              publicationYear,
              isrc,
              originalProducer,
              mainAudio,
              notes,
            }}
          >
            <FormExtras
              entity="Phonogram"
              mode="update"
              recordId={recordId}
              values={{
                trackName,
                albumName,
                publicationYear,
                isrc,
                originalProducer,
                mainAudio,
                notes,
              }}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "trackName":
                    onTrackNameChange?.(valor);
                    break;
                  case "albumName":
                    onAlbumNameChange?.(valor);
                    break;
                  case "publicationYear":
                    onPublicationYearChange?.(valor);
                    break;
                  case "isrc":
                    onIsrcChange?.(valor);
                    break;
                  case "originalProducer":
                    onOriginalProducerChange?.(valor);
                    break;
                  case "mainAudio":
                    onMainAudioChange?.(valor);
                    break;
                  case "notes":
                    onNotesChange?.(valor);
                    break;
                }
              }}
            />

            <GroupContainer
              id="phonogramInfo"
              errorText={phonogramUpdateStore.getCardErrors("phonogramInfo")}
              title={langStore.groupPhonogramPhonogramInfoLabel}
              collapsible={false}
              expanded={phonogramUpdateStore.getCardState("phonogramInfo")}
              toggleExpand={() =>
                phonogramUpdateStore.toggleCardState("phonogramInfo")
              }
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={trackName}
                  error={Boolean(trackNameError)}
                  warning={
                    !trackNameError &&
                    Boolean(phonogramUpdateStore.warnings["trackName"])
                  }
                  helperText={
                    trackNameError ||
                    phonogramUpdateStore.warnings["trackName"]?.message
                  }
                  label={langStore.PhonogramTrackNameLabel}
                  onChange={(value: string) => onTrackNameChange(value)}
                  description={langStore.PhonogramTrackNameDescription}
                />
                <InputReferenceSet
                  id="performers"
                  label={langStore.PhonogramPerformersLabel}
                  error={Boolean(performersError)}
                  helperText={
                    performersError ||
                    phonogramUpdateStore.warnings["performers"]?.message
                  }
                  selectedValues={performersSelectedValues}
                  onChange={(value: string) => onPerformersChange(value)}
                  onSelect={(value: { displayId: string; id: string }[]) =>
                    onPerformersSelected(value)
                  }
                  options={performersOptions}
                  description={langStore.PhonogramPerformersDescription}
                  nullText={langStore.optionalFieldHint}
                  canCreate={rootStore.authStore.hasPermissions(
                    "interpreter_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new InterpreterCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <InterpreterCreate
                          interpreterCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewInterpreter"
                      cancelMethod="cancelNewInterpreter"
                    />
                  )}
                />
                <InputString
                  value={albumName}
                  error={Boolean(albumNameError)}
                  warning={
                    !albumNameError &&
                    Boolean(phonogramUpdateStore.warnings["albumName"])
                  }
                  helperText={
                    albumNameError ||
                    phonogramUpdateStore.warnings["albumName"]?.message
                  }
                  label={langStore.PhonogramAlbumNameLabel}
                  onChange={(value: string) => onAlbumNameChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.PhonogramAlbumNameDescription}
                />
                <InputNumber
                  value={publicationYear}
                  nullable={true}
                  error={Boolean(publicationYearError)}
                  warning={
                    !publicationYearError &&
                    Boolean(phonogramUpdateStore.warnings["publicationYear"])
                  }
                  helperText={
                    publicationYearError ||
                    phonogramUpdateStore.warnings["publicationYear"]?.message
                  }
                  label={langStore.PhonogramPublicationYearLabel}
                  decimalScale={0}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.PhonogramPublicationYearDescription}
                  onChange={(value: number | undefined) =>
                    onPublicationYearChange(value)
                  }
                />
                <PhonogramIsrcCustomField
                  value={isrc}
                  error={Boolean(isrcError)}
                  helperText={
                    isrcError || phonogramUpdateStore.warnings["isrc"]?.message
                  }
                  {...{ optional: true }}
                  label={langStore.PhonogramIsrcLabel}
                  description={langStore.PhonogramIsrcDescription}
                  onChange={(value: string) => onIsrcChange(value)}
                />
                <InputString
                  value={originalProducer}
                  error={Boolean(originalProducerError)}
                  warning={
                    !originalProducerError &&
                    Boolean(phonogramUpdateStore.warnings["originalProducer"])
                  }
                  helperText={
                    originalProducerError ||
                    phonogramUpdateStore.warnings["originalProducer"]?.message
                  }
                  label={langStore.PhonogramOriginalProducerLabel}
                  onChange={(value: string) => onOriginalProducerChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.PhonogramOriginalProducerDescription}
                />
                <LoadFilesCRUDCompact
                  rows={audioFileFiles}
                  maxRows={1}
                  acceptFileTypes=".mp3,.m4a"
                  strictAccept={true}
                  menuActions={getViewAction(
                    (props) =>
                      phonogramUpdateStore.fileDownload(props, "audioFile"),
                    (props) =>
                      phonogramUpdateStore.viewFilePdf(props, "audioFile")
                  )}
                  showAddCommand={true}
                  showDeleteCommand={true}
                  label={langStore.PhonogramAudioFileLabel}
                  description={langStore.PhonogramAudioFileDescription}
                  onLoadFile={(file: File) => audioFileOnLoadFile(file)}
                  columns={[
                    { name: "mimetype", title: langStore.fileTypeLabel },
                    { name: "name", title: langStore.fileNameLabel },
                    { name: "formattedSize", title: langStore.fileSizeLabel },
                  ]}
                  onRestore={(id: string) => audioFileOnRestoreFile(id)}
                  onCommitChanges={(changes: GridChangeSet) =>
                    audioFileOnCommitFile(changes)
                  }
                  dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                  error={audioFileError}
                />
                <InputBoolean
                  value={mainAudio}
                  error={Boolean(mainAudioError)}
                  helperText={
                    mainAudioError ||
                    phonogramUpdateStore.warnings["mainAudio"]?.message
                  }
                  label={langStore.PhonogramMainAudioLabel}
                  description={langStore.PhonogramMainAudioDescription}
                  onChange={(value: boolean) => onMainAudioChange(value)}
                />
                <ItemsCRUDCompact
                  label={langStore.PhonogramLinksLabel}
                  description={langStore.PhonogramLinksDescription}
                  showLabel={true}
                  rows={linksRows}
                  columns={linksVisibleColumns}
                  columnTypes={linksColumnTypes}
                  canCreate={true}
                  isEditVisible={() =>
                    rootStore.authStore.hasPermissions("musicalWorkLink_update")
                  }
                  isDeleteVisible={() =>
                    rootStore.authStore.hasPermissions("musicalWorkLink_delete")
                  }
                  popupComponent={(row: any) => {
                    if (
                      row &&
                      row.id &&
                      !(
                        typeof row.id === "string" && row.id.startsWith("auto-")
                      )
                    ) {
                      runInAction(() => phonogramUpdateStore.linksLoadRow(row));
                    }
                    return (
                      <MusicalWorkLinkUpdate
                        musicalWorkLinkUpdateStore={musicalWorkLinkUpdateStore}
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    );
                  }}
                  popupMaxWidth="sm"
                  onRestore={(id: string) => linksOnRestore(id)}
                  onCommitChanges={(changes: GridChangeSet) =>
                    linksOnCommitChanges(changes)
                  }
                  onCommitInlineChange={(
                    rowId: string,
                    changes: Record<string, any>
                  ) => linksOnCommitInlineChange(rowId, changes)}
                  validate={(changes: GridChangeSet) =>
                    linksValidateChanges(changes)
                  }
                  dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                  error={linksError}
                />
                <InputText
                  value={notes}
                  error={Boolean(notesError)}
                  warning={
                    !notesError &&
                    Boolean(phonogramUpdateStore.warnings["notes"])
                  }
                  helperText={
                    notesError ||
                    phonogramUpdateStore.warnings["notes"]?.message
                  }
                  label={langStore.PhonogramNotesLabel}
                  onChange={(value: string) => onNotesChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.PhonogramNotesDescription}
                />
              </FormControl>
            </GroupContainer>
          </FormRecordProvider>
        </Box>
        {(showSaveFooter ?? !showToolbox) && (
          <FormSaveFooter
            onSave={() => onSave()}
            label={langStore.saveEntity}
            disabled={saving}
          />
        )}
        <PDFViewerDialog
          open={phonogramUpdateStore.pdfViewerOpen}
          onClose={() => phonogramUpdateStore.closePdfViewer()}
          pdfUrl={phonogramUpdateStore.pdfViewerUrl}
          title={phonogramUpdateStore.pdfViewerTitle}
        />
      </Grid>
    );
  }
);
